import React, { useLayoutEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { message, Select } from "antd";
import {
    useUpdatePasswordMutation,
    useUpdateAddressMutation,
    useGetGovernorateQuery,
} from "../../RTK/Slices/Slices/apiSliceMeat";
import { language } from "../../language/Language";
import { useDispatch } from "react-redux";
import { reduxLogout } from "../../RTK/Slices/authSlice";

const { Option } = Select;
const isAr = language === "ar";

const AVATAR = "https://st3.depositphotos.com/15648834/17930/v/600/depositphotos_179308454-stock-illustration-unknown-person-silhouette-glasses-profile.jpg";

/* ── أنماط مشتركة ── */
const card: React.CSSProperties = { background:"#fff", borderRadius:14, padding:"20px 22px", marginBottom:16, boxShadow:"0 2px 12px rgba(0,0,0,0.07)" };
const lbl: React.CSSProperties  = { fontSize:13, color:"#888", marginBottom:4, display:"block" };
const val: React.CSSProperties  = { fontSize:15, fontWeight:600, color:"#222" };
const inputStyle: React.CSSProperties = { width:"100%", padding:"11px 14px", border:"1px solid #ddd", borderRadius:8, fontSize:15, fontFamily:"Cairo, sans-serif", boxSizing:"border-box", outline:"none", direction:"rtl", backgroundColor:"#f9f9f9" };
const redBtn: React.CSSProperties  = { background:"linear-gradient(135deg,#c0392b,#922b21)", color:"#fff", border:"none", borderRadius:10, padding:"12px 0", width:"100%", fontSize:16, fontWeight:"bold", cursor:"pointer", fontFamily:"Cairo, sans-serif" };
const outBtn: React.CSSProperties  = { background:"transparent", color:"#c0392b", border:"2px solid #c0392b", borderRadius:10, padding:"10px 0", width:"100%", fontSize:15, fontWeight:"bold", cursor:"pointer", fontFamily:"Cairo, sans-serif" };

const SectionTitle = ({ emoji, text }: { emoji: string; text: string }) => (
    <h3 style={{ color:"#c0392b", fontSize:16, fontWeight:"bold", marginBottom:14, borderBottom:"1px solid #f0e0e0", paddingBottom:10 }}>
        {emoji} {text}
    </h3>
);

const EyeBtn = ({ show, toggle }: { show: boolean; toggle: () => void }) => (
    <button type="button" onClick={toggle} style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:16, color:"#999", padding:0 }}>
        {show ? "👁" : "🙈"}
    </button>
);

/* ════════════════════════════════════════════════ */
const ProfileUser: React.FC = () => {
    const navigate  = useNavigate();
    const dispatch  = useDispatch();

    const [updatePassword, { isLoading: savingPw }]   = useUpdatePasswordMutation();
    const [updateAddress,  { isLoading: savingAddr }]  = useUpdateAddressMutation();
    const { data: govData, isLoading: govLoading }     = useGetGovernorateQuery(undefined);

    /* ── بيانات المستخدم من localStorage ── */
    const userData = useMemo(() => {
        try { const s = localStorage.getItem("user"); return s ? JSON.parse(s) : {}; }
        catch { return {}; }
    }, []);

    useLayoutEffect(() => { if (!userData?.name) navigate("/Login"); }, [navigate, userData]);

    const activeGovs = useMemo(() =>
        (govData?.data ?? []).filter((g: any) => g.status === 1),
        [govData]
    );

    /* ── تغيير كلمة المرور ── */
    const [showPw, setShowPw]       = useState(false);
    const [curShow, setCurShow]     = useState(false);
    const [newShow, setNewShow]     = useState(false);
    const [conShow, setConShow]     = useState(false);
    const [pwForm, setPwForm]       = useState({ current_password:"", new_password:"", confirm_password:"" });

    /* ── تعديل العنوان ── */
    const [showAddr, setShowAddr]   = useState(false);
    const [addrForm, setAddrForm]   = useState({
        governorate: userData?.governorate?.id ?? "",
        address:     userData?.address  ?? "",  // اسم الشارع
        city:        userData?.city     ?? "",  // رقم المبنى
        street:      userData?.street   ?? "",  // رقم الدور
        building:    userData?.building ?? "",  // رقم الشقة
    });

    const handlePwChange   = (e: React.ChangeEvent<HTMLInputElement>) =>
        setPwForm(p => ({ ...p, [e.target.name]: e.target.value }));
    const handleAddrChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        setAddrForm(p => ({ ...p, [e.target.name]: e.target.value }));

    /* ── حفظ كلمة المرور ── */
    const handleSavePw = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password)
            return message.error(isAr ? "يرجى ملء جميع الحقول" : "Fill all fields");
        if (pwForm.new_password !== pwForm.confirm_password)
            return message.error(isAr ? "كلمة المرور الجديدة غير متطابقة" : "Passwords don't match");
        if (pwForm.new_password.length < 4)
            return message.error(isAr ? "كلمة المرور 4 أحرف على الأقل" : "Min 4 chars");
        try {
            const res: any = await updatePassword({ current_password: pwForm.current_password, new_password: pwForm.new_password }).unwrap();
            if (res?.success === 1) {
                message.success(isAr ? "تم تغيير كلمة المرور بنجاح ✅" : "Password updated");
                setPwForm({ current_password:"", new_password:"", confirm_password:"" });
                setShowPw(false);
            } else {
                message.error(res?.msg || (isAr ? "كلمة المرور الحالية غير صحيحة" : "Wrong current password"));
            }
        } catch { message.error(isAr ? "فشل تغيير كلمة المرور" : "Failed"); }
    };

    /* ── حفظ العنوان ── */
    const handleSaveAddr = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!addrForm.governorate || !addrForm.city || !addrForm.street || !addrForm.building)
            return message.error(isAr ? "يرجى ملء جميع الحقول الإلزامية" : "Fill required fields");
        try {
            const res: any = await updateAddress({
                ...addrForm,
                contact_id: userData?.contact_id,
            }).unwrap();
            if (res?.success === 1) {
                // تحديث localStorage بالبيانات الجديدة
                const updated = { ...userData, ...res.user };
                localStorage.setItem("user", JSON.stringify(updated));
                message.success(isAr ? "تم تحديث العنوان بنجاح ✅" : "Address updated");
                setShowAddr(false);
                // إعادة تحميل الصفحة لتحديث بيانات العرض
                setTimeout(() => window.location.reload(), 800);
            } else {
                message.error(res?.msg || (isAr ? "فشل تحديث العنوان" : "Failed"));
            }
        } catch { message.error(isAr ? "فشل تحديث العنوان" : "Failed"); }
    };

    const handleLogout = useCallback(() => {
        localStorage.removeItem("user");
        dispatch(reduxLogout());
        navigate("/Login");
    }, [dispatch, navigate]);

    const fullAddress = useMemo(() => {
        const parts: string[] = [];
        if (userData?.governorate?.name) parts.push(userData.governorate.name);
        if (userData?.address)           parts.push(`شارع ${userData.address}`);
        if (userData?.city)              parts.push(`عمارة ${userData.city}`);
        if (userData?.street)            parts.push(`دور ${userData.street}`);
        if (userData?.building)          parts.push(`شقة ${userData.building}`);
        return parts.join(" - ") || "—";
    }, [userData]);

    return (
        <div dir="rtl" style={{ minHeight:"100vh", background:"linear-gradient(160deg,#fdf8f4,#f0ebe5)", fontFamily:"Cairo, sans-serif", paddingTop:90, paddingBottom:40 }}>
            <div style={{ maxWidth:460, margin:"0 auto", padding:"0 16px" }}>

                {/* الصورة والاسم */}
                <div style={{ ...card, textAlign:"center", paddingTop:28, paddingBottom:24 }}>
                    <img src={AVATAR} alt="avatar" style={{ width:90, height:90, borderRadius:"50%", objectFit:"cover", border:"3px solid #c0392b", marginBottom:12 }} />
                    <div style={{ fontSize:19, fontWeight:"bold", color:"#222" }}>{userData?.name}</div>
                    <div style={{ color:"#888", fontSize:13, marginTop:4 }}>{userData?.mobile}</div>
                </div>

                {/* بيانات الحساب */}
                <div style={card}>
                    <SectionTitle emoji="📋" text={isAr ? "بيانات الحساب" : "Account Info"} />
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                        <div><span style={lbl}>{isAr ? "الاسم" : "Name"}</span><span style={val}>{userData?.name || "—"}</span></div>
                        <div><span style={lbl}>{isAr ? "رقم الهاتف" : "Phone"}</span><span style={val}>{userData?.mobile || "—"}</span></div>
                        {userData?.email && <div style={{ gridColumn:"1/-1" }}><span style={lbl}>البريد</span><span style={val}>{userData.email}</span></div>}
                    </div>
                </div>

                {/* عنوان التوصيل */}
                <div style={card}>
                    <SectionTitle emoji="📍" text={isAr ? "عنوان التوصيل" : "Delivery Address"} />

                    {!showAddr ? (
                        <>
                            <p style={{ color:"#555", fontSize:14, marginBottom:14, lineHeight:1.8 }}>{fullAddress}</p>
                            <button style={outBtn} onClick={() => setShowAddr(true)}>
                                ✏️ {isAr ? "تعديل العنوان" : "Edit Address"}
                            </button>
                        </>
                    ) : (
                        <form onSubmit={handleSaveAddr}>

                            {/* المنطقة */}
                            <div style={{ marginBottom:14 }}>
                                <label style={{ ...lbl, fontSize:14, fontWeight:600, color:"#555" }}>
                                    {isAr ? "منطقة التوصيل" : "Delivery Area"} <span style={{ color:"#c0392b" }}>*</span>
                                </label>
                                <Select
                                    showSearch
                                    placeholder="اختر منطقتك من القائمة"
                                    optionFilterProp="label"
                                    loading={govLoading}
                                    value={addrForm.governorate || undefined}
                                    onChange={(v) => setAddrForm(p => ({ ...p, governorate: v }))}
                                    filterOption={(inp, opt) => (opt?.label as string)?.toLowerCase().includes(inp.toLowerCase())}
                                    style={{ width:"100%", height:46 }}
                                >
                                    {activeGovs.map((g: any) => (
                                        <Option key={g.id} value={g.id} label={g.name}>{g.name}</Option>
                                    ))}
                                </Select>
                            </div>

                            {/* اسم الشارع */}
                            <div style={{ marginBottom:14 }}>
                                <label style={{ ...lbl, fontSize:14, fontWeight:600, color:"#555" }}>
                                    {isAr ? "اسم الشارع (اختياري)" : "Street Name"}
                                </label>
                                <input name="address" value={addrForm.address} onChange={handleAddrChange}
                                    placeholder="اسم الشارع" style={inputStyle} />
                            </div>

                            {/* رقم المبنى */}
                            <div style={{ marginBottom:14 }}>
                                <label style={{ ...lbl, fontSize:14, fontWeight:600, color:"#555" }}>
                                    {isAr ? "رقم المبنى" : "Building No."} <span style={{ color:"#c0392b" }}>*</span>
                                </label>
                                <input name="city" value={addrForm.city} onChange={handleAddrChange}
                                    placeholder="رقم المبنى" style={inputStyle} required />
                            </div>

                            {/* رقم الدور + رقم الشقة */}
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>
                                <div>
                                    <label style={{ ...lbl, fontSize:14, fontWeight:600, color:"#555" }}>
                                        {isAr ? "رقم الدور" : "Floor"} <span style={{ color:"#c0392b" }}>*</span>
                                    </label>
                                    <input name="street" value={addrForm.street} onChange={handleAddrChange}
                                        placeholder="رقم الدور" style={inputStyle} required />
                                </div>
                                <div>
                                    <label style={{ ...lbl, fontSize:14, fontWeight:600, color:"#555" }}>
                                        {isAr ? "رقم الشقة" : "Apt."} <span style={{ color:"#c0392b" }}>*</span>
                                    </label>
                                    <input name="building" value={addrForm.building} onChange={handleAddrChange}
                                        placeholder="رقم الشقة" style={inputStyle} required />
                                </div>
                            </div>

                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                                <button type="submit" disabled={savingAddr}
                                    style={{ ...redBtn, opacity: savingAddr ? 0.7 : 1 }}>
                                    {savingAddr ? "..." : (isAr ? "حفظ العنوان" : "Save")}
                                </button>
                                <button type="button"
                                    onClick={() => setShowAddr(false)}
                                    style={outBtn}>
                                    {isAr ? "إلغاء" : "Cancel"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                {/* تغيير كلمة المرور */}
                <div style={card}>
                    <SectionTitle emoji="🔑" text={isAr ? "كلمة المرور" : "Password"} />
                    {!showPw ? (
                        <button style={outBtn} onClick={() => setShowPw(true)}>
                            {isAr ? "تغيير كلمة المرور" : "Change Password"}
                        </button>
                    ) : (
                        <form onSubmit={handleSavePw}>
                            {[
                                { name:"current_password", label:"كلمة المرور الحالية", show:curShow, toggle:() => setCurShow(p => !p) },
                                { name:"new_password",     label:"كلمة المرور الجديدة", show:newShow, toggle:() => setNewShow(p => !p) },
                                { name:"confirm_password", label:"تأكيد كلمة المرور",   show:conShow, toggle:() => setConShow(p => !p) },
                            ].map(({ name, label, show, toggle }) => (
                                <div key={name} style={{ marginBottom:14 }}>
                                    <label style={{ ...lbl, fontSize:14, fontWeight:600, color:"#555" }}>{label}</label>
                                    <div style={{ position:"relative" }}>
                                        <input name={name} type={show ? "text" : "password"}
                                            value={(pwForm as any)[name]} onChange={handlePwChange}
                                            placeholder={label} inputMode="numeric"
                                            style={{ ...inputStyle, paddingLeft:40 }} required />
                                        <EyeBtn show={show} toggle={toggle} />
                                    </div>
                                </div>
                            ))}
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:4 }}>
                                <button type="submit" disabled={savingPw} style={{ ...redBtn, opacity: savingPw ? 0.7 : 1 }}>
                                    {savingPw ? "..." : (isAr ? "حفظ" : "Save")}
                                </button>
                                <button type="button" onClick={() => { setShowPw(false); setPwForm({ current_password:"", new_password:"", confirm_password:"" }); }} style={outBtn}>
                                    {isAr ? "إلغاء" : "Cancel"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                {/* تسجيل الخروج */}
                <button onClick={handleLogout} style={{ ...outBtn, marginBottom:20 }}>
                    🚪 {isAr ? "تسجيل الخروج" : "Logout"}
                </button>

            </div>
        </div>
    );
};

export default ProfileUser;
